import {
  createSelector,
  createSlice,
  original,
  PayloadAction
} from '@reduxjs/toolkit';
import { commandApi } from 'app/apis/commandApi';
import { RootState } from 'app/store';
import { selectSite } from 'features/topbar/selectedSiteSlice';
import { PURGE } from 'redux-persist';

export interface BasePendingDeleteRow {
  rowId: string;
}

export interface RowPendingDeleteState {
  componentId?: string;
  rows: BasePendingDeleteRow[];
}

const initialState = {
  rowsPendingDelete: [] as RowPendingDeleteState[]
};

const rowsPendingDeleteSlice = createSlice({
  name: 'rowsPendingDeleteSlice',
  initialState,
  reducers: {
    clearPendingDeletes: (
      state,
      { payload: { componentId } }: PayloadAction<{ componentId: string }>
    ) => {
      state.rowsPendingDelete = state.rowsPendingDelete.filter(
        (selection) => selection.componentId !== componentId
      );
    }
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
    builder.addCase(selectSite, (state, action) => initialState);
    builder.addMatcher(
      commandApi.endpoints.deleteMultipleRevenues.matchFulfilled,
      (
        state,
        {
          meta: {
            arg: {
              originalArgs: { componentId, revenue }
            }
          }
        }
      ) => {
        const currentRowSelection = state.rowsPendingDelete.find(
          (rowSelection) => rowSelection.componentId === componentId
        );
        if (!currentRowSelection) {
          state.rowsPendingDelete.push({
            componentId,
            rows: revenue.map((rev) => ({
              rowId: rev.revenueId
            }))
          });
          return;
        }

        const rowsToAdd = revenue
          .map((rev) => ({
            rowId: rev.revenueId
          }))
          .filter((revenueRows) =>
            currentRowSelection?.rows.some(
              (row) => row.rowId !== revenueRows.rowId
            )
          );

        currentRowSelection.rows = [...currentRowSelection.rows, ...rowsToAdd];
      }
    );
    builder.addMatcher(
      commandApi.endpoints.deleteMultipleOpportunities.matchFulfilled,
      (
        state,
        {
          meta: {
            arg: {
              originalArgs: { componentId, opportunities }
            }
          }
        }
      ) => {
        const currentRowSelection = state.rowsPendingDelete.find(
          (rowSelection) => rowSelection.componentId === componentId
        );
        if (!currentRowSelection) {
          state.rowsPendingDelete.push({
            componentId,
            rows: opportunities.map((opp) => ({
              rowId: opp.opportunityId
            }))
          });
          return;
        }

        const rowsToAdd = opportunities
          .map((opp) => ({
            rowId: opp.opportunityId
          }))
          .filter((opportunityRows) =>
            currentRowSelection?.rows.some(
              (row) => row.rowId !== opportunityRows.rowId
            )
          );

        currentRowSelection.rows = [...currentRowSelection.rows, ...rowsToAdd];
      }
    );
  }
});

const selectSelf = (state: RootState) => state.rowsPendingDelete;
const selectRowsPendingDeleteByComponentId = (
  state: RootState,
  componentId: string
) => componentId;

export const selectRowsPendingDeleteForComponent = createSelector(
  selectSelf,
  selectRowsPendingDeleteByComponentId,
  (state, componentId) => {
    const pendingRows = state.rowsPendingDelete.find(
      (rowSelection) => rowSelection.componentId === componentId
    );
    return pendingRows?.rows ?? [];
  }
);

export const selectDoIHavePendingDeletesForComponent = createSelector(
  selectSelf,
  selectRowsPendingDeleteByComponentId,
  (state, componentId) =>
    state.rowsPendingDelete.some(
      (rowSelection) =>
        rowSelection.componentId === componentId && rowSelection.rows.length > 0
    )
);

const { actions, reducer } = rowsPendingDeleteSlice;

export const { clearPendingDeletes } = actions;

export default reducer;
